import config from 'config';

import {
  EventSchema,
  EventStateSchema,
  EventStatus,
  EventUserSchema,
  LiveEventTokenSource,
  UserRole,
  UserSchema,
  VideoLayout
} from 'types';
import { EventSlice, EventSliceSession } from 'store/slices/events';

export const MAX_STAGE_SEATS = 6;

export const EMPTY_EVENT: EventSchema = {
  channelId: '',
  channelName: '',
  code: '',
  description: '',
  endDate: '',
  hostAvatar: '',
  hostUsername: '',
  hostName: '',
  ivsIngestServer: '',
  ivsPlaybackUrl: '',
  ivsStreamKey: '',
  name: '',
  poster: '',
  startDate: '',
  status: EventStatus.SCHEDULED,
  handRaiseEnabled: false,
  recordingPlaybackUrl: '',
  memberOnly: false,
  recordingEnabled: false,
  hidePastEvent: null
};

export const EMPTY_EVENT_STATE: EventStateSchema = {
  channel: '',
  description: '',
  status: EventStatus.STARTED,
  startDate: new Date().toISOString(),
  endDate: '',
  eventCode: '',
  eventName: '',
  handRaiseEnabled: false,
  recordingEnabled: false,
  onStageUsers: { hosts: [], moderators: [], speakers: [] },
  invitees: [],
  joinRequests: [],
  hiddenJoinRequests: [],
  pendingUsers: [],
  viewers: []
};

export const EMPTY_EVENT_SESSION: EventSliceSession = {
  username: null,
  uid: 0,
  token: '',
  tokenSource: LiveEventTokenSource.AUTH
};

export const EMPTY_EVENT_SLICE: EventSlice = {
  session: EMPTY_EVENT_SESSION,
  meta: EMPTY_EVENT,
  state: EMPTY_EVENT_STATE
};

export const EMPTY_EVENT_USER: EventUserSchema = {
  username: '',
  name: '',
  avatar: '',
  role: UserRole.VIEWER,
  member: false,
  invitee: false,
  eventCode: '',
  inviteeCode: '',
  timestamp: '',
  joinedOnStageDate: ''
};

export const EMPTY_USER: UserSchema = {
  id: '',
  name: '',
  role: UserRole.VIEWER,
  avatar: '',
  phone: '',
  email: '',
  username: '',
  channelId: '',
  channelName: '',
  availableMinutes: 0,
  createdDate: '',
  onFreeTrial: false,
  currentPlan: null,
  membersOnlyEnabled: false,
  membershipChannels: []
};

export const TOKEN = `Bearer`;
export const SELECTED_ACTIVE_SPEAKER_TIMEOUT = 5000; // ms
export const TOAST_LIMIT = 3;
export const PAGINATION_SIZE = 10;

/**
 * Agora suggests 5
 * link: https://docs.agora.io/en/Interactive%20Broadcast/API%20Reference/web_ng/interfaces/iagorartcclient.html#event_volume_indicator
 */
export const ACTIVE_VOLUME_THRESHOLD = 5; // range: 0-100

export const RTM_CHANNEL_MEMBER_LIMIT = 200;

export const IMAGE_SIZE_LIMIT = 2 * 1024 * 1024; // 2 MB

export const EVENT_POSTER_SIZE_LIMIT = 300 * 1024; // 300 Kb
export const EVENT_POSTER_SUPPORTED_FORMATS = ['png', 'jpg', 'jpeg'];
export const EVENT_TITLE_MAX_LENGTH = 50;
export const INVITEE_NAME_MAX_LENGTH = 50;
export const EVENT_DESCRIPTION_MAX_LENGTH = 500;

export const SCHEDULED_EVENT_OVERDUE = 2 * 60 * 60 * 1_000; // 120 min === 2 hr

export const CHANNEL_NAME_MAX_LENGTH = 20;
export const CHANNEL_DESCRIPTION_MAX_LENGTH = 500;
export const CHANNEL_LOGO_SIZE_LIMIT = 2 * 1024 * 1024; // 2 Mb
export const CHANNEL_LOGO_SUPPORTED_FORMATS = ['png', 'jpg', 'jpeg'];

export const VIDEO_LAYOUT: { [key: string]: VideoLayout } = {
  1: {
    shape: [1, 1],
    grid: [[1]]
  },
  2: {
    shape: [2, 1],
    grid: [[1], [1]]
  },
  3: {
    shape: [2, 2],
    grid: [
      [1, 1],
      [1, 1]
    ]
  },
  4: {
    shape: [2, 2],
    grid: [
      [1, 1],
      [1, 1]
    ]
  },
  5: {
    shape: [3, 2],
    grid: [
      [1, 1],
      [1, 1],
      [1, 1]
    ]
  },
  6: {
    shape: [3, 2],
    grid: [
      [1, 1],
      [1, 1],
      [1, 1]
    ]
  }
};

export const TWITCH_RTMP_URL = 'rtmps://sfo.contribute.live-video.net/app';
export const TWITCH_AUTH_BASE_URL = 'https://id.twitch.tv/oauth2/authorize';

export const EVENT_API_VERSION = 'v1';
export const EVENT_API_BASE_PATH = `/event/${EVENT_API_VERSION}`;
export const TOKEN_API_VERSION = 'v1';
export const TOKEN_API_BASE_PATH = `/token/${TOKEN_API_VERSION}`;
export const USER_API_VERSION = 'v1';
export const USER_API_BASE_PATH = `/user/${USER_API_VERSION}`;
export const CHANNEL_API_VERSION = 'v1';
export const CHANNEL_API_BASE_PATH = `/channel/${CHANNEL_API_VERSION}`;
export const PRICING_PLAN_API_VERSION = 'v1';
export const PRICING_PLAN_API_BASE_PATH = `/pricing/plan/${PRICING_PLAN_API_VERSION}`;
export const PAYMENT_API_VERSION = 'v1';
export const PAYMENT_API_BASE_PATH = `/payment/${PAYMENT_API_VERSION}`;

export const SURVEY_LINK = 'https://forms.gle/zz7Qqo52j9mP8tJX9';

export const EXTERNAL_LINKS = {
  ABOUT: 'https://info.livetalks.tv',
  CAREERS: 'https://info.livetalks.tv/careers.html',
  WAITING_LIST: 'https://share.hsforms.com/1nPhbbBPKRnuyuKip-_NaTA2tlkf',
  INFO_UZBEKITTV_LINK: 'https://info.livetalks.tv/uzbekittv.html'
};

export const UZBEKITTV_CHANNEL_NAME = 'uzbekittv';
export const UZBEKITTV_FEE_IN_UZS = config.uzbekittv.fee_in_uzs;

export const MAX_VIEWER_COUNT_UPDATE_INTERVAL = 30 * 1_000; // 30 sec

export const SHOW_BECOME_MEMBER_MODAL_INTERVAL = 10 * 60 * 1_000; // 10 minutes

export const WARN_LIVE_EVENT_AUTO_ENDING_BEFORE = 10 * 60 * 1_000; // 10 minutes

export const PREVENT_ENDING_EVENT_IN = 60 * 1_000; // 1 minute

export const TRANSCODING_USER_AVATAR_SIZE = 90;
export const TRANSCODING_USER_AVATAR_URL = `${config.app.url}/assets/images/camera-off.png`;

// Colors are generated using https://coolors.co
export const PALETTE_COLORS = [
  '#A31621',
  '#00A878',
  '#F86624',
  '#9649CB',
  '#256EFF',
  '#05299E',
  '#F58A07',
  '#662E9B',
  '#56AA4B',
  '#0CBABA'
];
